"use client";

import { createContext, ReactNode, useContext } from "react";

/**
 * Studio mode renders exactly the same landing components as the public page, but tags
 * every component boundary with the path of the node it came from in the page JSON.
 * Clicking an element in the studio frame then yields an exact path such as
 * `components[0].content.data[2].components[1]`, which is what an agent needs in order to
 * edit the right node instead of guessing from a description.
 *
 * The context value is the path of the node currently being rendered. `null` means studio
 * is off — the case on every public page — so nothing is tagged and no extra attribute
 * reaches the HTML.
 */
const StudioNodePathContext = createContext<string | null>(null);

export function StudioNodePath({ path, children }: { path: string | null; children: ReactNode }) {
    return <StudioNodePathContext.Provider value={path}>{children}</StudioNodePathContext.Provider>;
}

export function useStudioNodePath() {
    return useContext(StudioNodePathContext);
}

/**
 * A registry item inside a `components` array. The root loader renders with an empty
 * parent path, so its items are `components[i]`; a loader inside a block cell continues
 * that cell's path.
 */
export function componentNodePath(parentPath: string | null, index: number) {
    if (parentPath === null) return null;
    return parentPath === "" ? `components[${index}]` : `${parentPath}.components[${index}]`;
}

/** A cell of a block component. */
export function blockCellNodePath(blockPath: string | null, index: number) {
    if (blockPath === null) return null;
    return `${blockPath}.content.data[${index}]`;
}

export const STUDIO_NODE_SELECTOR = "[data-lb-path]";

/**
 * Spread onto the wrapper a component already renders. Returns `undefined` outside studio
 * mode, and `{...undefined}` adds nothing, so public markup stays exactly as it was.
 */
export function studioNodeAttributes(path: string | null, name: string, id?: number | string) {
    if (path === null) return undefined;
    return {
        "data-lb-path": path,
        "data-lb-name": name,
        "data-lb-id": id === undefined || id === null ? undefined : String(id),
    };
}
